import useUpdateEffect from '@pretto/bricks/core/utility/useUpdateEffect'

import {
  Illustration,
  InputComponent,
} from '@pretto/website/src/templates/blocs/components/simulatorRow/SimulatorRow.types'
import { formatCloudFunctionRatesToAdvancedSimuRates } from '@pretto/website/src/templates/simulateurAvance/lib/formatCloudFunctionRatesToAdvancedSimuRates'
import { useTracking } from '@pretto/website/src/utilities'

import { useEffect, useMemo, useRef, useState } from 'react'

import * as INPUTS from '../config/inputs'
import { CONFIDENCE_LEVEL_INCREMENT, CONFIDENCE_LEVEL_START, ROUND_COST, ROUND_PAYMENT } from '../config/static'
import * as calc from '../lib/calc'
import { filterGraphResults, formatBars, getDefaultIncome, getError, getMatch, getSteps } from '../lib/helpers'

import { useRadio } from './useRadio'
import { useSlider } from './useSlider'

export const usePurchase = ({ trackButtonClick }, marketRatesProfileLatest) => {
  const trackAction = useTracking()
  const miniSimulator = formatCloudFunctionRatesToAdvancedSimuRates(marketRatesProfileLatest)
  const [rate, setRate] = useState(0)
  const [payment, setPayment] = useState(0)
  const [interests, setInterests] = useState(0)
  const [errorFlag, setErrorFlag] = useState()
  const [keyChanged, setKeyChanged] = useState({})

  const handleClickResultsButton = () => {
    trackButtonClick('results')
  }

  const {
    value: numberMortgagors,
    props: numberMortgagorsProps,
    inputType: numberMortgagorsInputType,
  } = useRadio('numberMortgagors')

  const {
    value: amount,
    debouncedValue: debouncedAmount,
    props: amountProps,
    inputType: amountInputType,
  } = useSlider('amount')

  const {
    value: contribution,
    debouncedValue: debouncedContribution,
    props: contributionProps,
    inputType: contributionInputType,
  } = useSlider('contribution')

  const {
    value: duration,
    debouncedValue: debouncedDuration,
    onChangeInput: onChangeDuration,
    props: durationProps,
    inputType: durationInputType,
  } = useSlider('duration')

  const {
    value: income,
    debouncedValue: debouncedIncome,
    props: incomeProps,
    inputType: incomeInputType,
  } = useSlider('income')

  const lastChangedInputTypes = {
    amount: amountInputType,
    contribution: contributionInputType,
    duration: durationInputType,
    income: incomeInputType,
    numberMortgagors: numberMortgagorsInputType,
  }

  const loanAmount = calc.loanAmount({ contribution, goodAmount: amount })
  const defaultIncome = getDefaultIncome(loanAmount)

  const getResultGraph = (income = defaultIncome) =>
    filterGraphResults({
      duration,
      income,
      miniSimulator,
      numberMortgagors,
    })

  const defaultResultsGraph = getResultGraph()

  const defaultBars = formatBars({
    loanAmount,
    results: defaultResultsGraph,
    selectedDuration: parseInt(duration),
  })

  const [bars, setBars] = useState(defaultBars)

  const previousData = useRef({ amount, contribution, duration, income, numberMortgagors })

  const match = (income = defaultIncome) => getMatch({ duration, income, miniSimulator, numberMortgagors })

  useEffect(() => {
    const { rate } = match()
    const payment = calc.payment({ duration, loanAmount, rate })
    setPayment(payment)
    setRate(rate)
    const interests = calc.creditCost({ duration, loanAmount, payment })
    setInterests(interests)
  }, [])

  useUpdateEffect(() => {
    const { max, min } = INPUTS.duration
    if (debouncedDuration < min || !debouncedDuration) {
      onChangeDuration(min)
      return
    }
    if (debouncedDuration > max) {
      onChangeDuration(max)
    }
  }, [debouncedDuration])

  useUpdateEffect(() => {
    const changes = { amount, contribution, duration, income, numberMortgagors }
    const valueType = Object.entries(changes).reduce((previous, [key, value]) => {
      if (value !== previousData.current[key]) {
        setKeyChanged(prev => ({ ...prev, [key]: true }))
        return key
      }

      return previous
    }, false)

    previousData.current = changes

    if (valueType) {
      const inputType = lastChangedInputTypes[valueType]

      trackAction(`Calculator ${inputType} ${valueType} changed`, {
        calculatorVersion: 'v2',
        [valueType]: parseInt(changes[valueType]),
      })
    }

    try {
      if (duration > INPUTS.duration.max || duration < INPUTS.duration.min) {
        return
      }

      const loanAmount = calc.loanAmount({ contribution, goodAmount: amount })

      if (loanAmount < 0) {
        setErrorFlag('contribution')
        return
      }

      const defaultIncome = getDefaultIncome(loanAmount)

      const dataIncome = income ?? defaultIncome

      const result = match(income)

      if (!result) {
        setErrorFlag('contribution')
        return
      }

      setRate(result.rate)

      const resultsGraph = getResultGraph(income)
      const payment = calc.payment({ duration, loanAmount, rate: result.rate })
      const errorFlag = getError({ income: dataIncome, numberMortgagors, payment })

      if (errorFlag) {
        setErrorFlag(errorFlag)
        return
      }

      const interests = calc.creditCost({ duration, loanAmount, payment })
      setErrorFlag('')
      setPayment(payment)
      setInterests(interests)

      const bars = formatBars({
        loanAmount,
        results: resultsGraph,
        selectedDuration: parseInt(duration),
      })

      setBars(bars)
    } catch (error) {
      if (error) {
        setErrorFlag('contribution')
      }
    }
  }, [debouncedAmount, debouncedDuration, debouncedIncome, debouncedContribution, numberMortgagors])

  const graphProps = useMemo(() => {
    const principalSteps = getSteps(bars, 'totalCost', ROUND_COST)
    const paymentSteps = getSteps(bars, 'totalPayment', ROUND_PAYMENT)

    return {
      bars,
      paymentSteps,
      principalSteps,
    }
  }, [bars])

  const confidenceLevel = Object.keys(keyChanged).reduce((previous, key) => {
    if (key !== 'duration') {
      return previous + CONFIDENCE_LEVEL_INCREMENT
    }
    return previous
  }, CONFIDENCE_LEVEL_START)

  const inputBlocks = [
    {
      illustration: Illustration.HOUSE,
      inputs: [
        {
          props: amountProps,
          type: InputComponent.SLIDER,
        },
        {
          props: durationProps,
          type: InputComponent.SLIDER,
        },
      ],
      title: 'Votre projet',
    },
    {
      illustration: parseInt(numberMortgagors) === 1 ? Illustration.SINGLE : Illustration.COUPLE,
      inputs: [
        {
          props: numberMortgagorsProps,
          type: InputComponent.RADIO,
        },
        {
          props: incomeProps,
          type: InputComponent.SLIDER,
        },
        {
          props: contributionProps,
          type: InputComponent.SLIDER,
        },
      ],
      title: 'Votre profil',
    },
  ]

  const isGraphActive = !errorFlag

  const resultProps = useMemo(
    () => ({
      confidenceLevel,
      creditCost: interests,
      onClickButton: handleClickResultsButton,
      payment,
      rate,
    }),
    [interests, rate, payment, confidenceLevel]
  )
  return { errorFlag, graphProps, inputBlocks, isGraphActive, resultProps }
}
